import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import {
  fixTimezoneOffsetYYYYMMDD,
  timerOptions,
} from '../../constants/constants';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import { IMultiSelectOption, IOption } from '../../models/ICommon';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { IHKRDashboardDataVM } from '../../models/response/IHKRDashboardDataResponse';
import { IOrderTableViewListVM } from '../../models/response/IOrderTableViewResponse';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllHKRDashboardDataState,
  GetAllOrderTableState,
  GetAllOrderTableViewState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import {
  IOrderTableList,
  IOrderTableListVM,
  ProcessList,
} from '../../models/response/IOrderTableResponse';
import { IActiveProcesses } from '../../models/response/IActiveTrayResponse';
const appConfig = config();

export class DashboardStore {
  initialStateValue = ObservableInitialState;
  orderTableViewList: IOrderTableViewListVM = GetAllOrderTableViewState;
  getAllOrderTableViewState = { ...this.initialStateValue };
  getOrderTableListServiceState = { ...this.initialStateValue };
  orderList: IOrderTableListVM = GetAllOrderTableState;
  ledBlinkState = { ...this.initialStateValue };
  getHKRDashboardDataServiceState = { ...this.initialStateValue };
  hkrDashboardDataList: IHKRDashboardDataVM = GetAllHKRDashboardDataState;
  getColumnListServiceState = { ...this.initialStateValue };
  columnList: string[] = undefined;

  constructor() {
    makeObservable(this, {
      orderTableViewList: observable,
      getAllOrderTableViewState: observable,
      GetOrderTableViewListService: action,
      reset: action,
      allOrderTableView: computed,
      getOrderTableListServiceState: observable,
      orderList: observable,
      allColumnOptions: computed,
      LEDFormInitialValue: computed,
      TimerOptions: computed,
      ledBlinkState: observable,
      LedBlinkService: action,
      resetLedBlinkState: action,
      GetHKRDashboardDataService: action,
      getHKRDashboardDataServiceState: observable,
      hkrDashboardDataList: observable,
      allHKRDashboardData: computed,
      allProcesses: computed,
      resetGetOrderTableListServiceState: action,
      GetColumnListService: action,
      getColumnListServiceState: observable,
      columnList: observable,
      columnListDetails: computed,
      allProcessHeaders: computed,
      visibleHKRColumnsList: computed,
      visibleOrderTableColumnsList: computed,
      resetStore: action,
    });
  }

  //#region Actions
  GetOrderTableViewListService = (
    TenantId: number,
    PageSize: number,
    PageNo: number,
    filters: string
  ) => {
    this.getOrderTableListServiceState.inProgress = true;
    const url = `${URLConstants.GetAllOrderTableViewList}?TenantId=${TenantId}&PageSize=${PageSize}&PageNo=${PageNo}&Filters=${filters}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IOrderTableListVM>>) => {
          if (response.data.Error) {
            this.getOrderTableListServiceState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.orderList = response.data.Data;
            this.getOrderTableListServiceState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getOrderTableListServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getOrderTableListServiceState.inProgress = false;
        })
      );
  };

  LedBlinkService = (tenantId: number, trayId: string, data: any) => {
    this.ledBlinkState.inProgress = true;
    const url = `${URLConstants.LedBlink}?trayId=${trayId}&TenantId=${tenantId}&ColorCode=${encodeURIComponent(data.ColorCode)}&Second=${data.Second}`;
    return baseService
      .postRequest(url, null)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.ledBlinkState.error = response.data.Message;
        } else {
          this.ledBlinkState.success = true;
        }
      })
      .catch((err: string) => {
        this.ledBlinkState.error = err;
      })
      .finally(
        action(() => {
          this.ledBlinkState.inProgress = false;
        })
      );
  };

  GetHKRDashboardDataService = (
    TenantId: number,
    PageSize: number,
    PageNo: number,
    filters: string
  ) => {
    this.getHKRDashboardDataServiceState.inProgress = true;
    const url = `${URLConstants.HKRDashboardData}?TenantId=${TenantId}&PageSize=${PageSize}&PageNo=${PageNo}&Filters=${filters}&t=${new Date().getTime()}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IHKRDashboardDataVM>>) => {
          if (response.data.Error) {
            this.getHKRDashboardDataServiceState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.hkrDashboardDataList = response.data.Data;
            this.getHKRDashboardDataServiceState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getHKRDashboardDataServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getHKRDashboardDataServiceState.inProgress = false;
        })
      );
  };

  GetColumnListService = (TenantId: number) => {
    // Clear the previous factory's columns so stale data is never shown
    this.columnList = undefined;
    this.getColumnListServiceState.inProgress = true;
    const url = `${URLConstants.GetColumnList}/${TenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
        if (response.data.Error) {
          this.getColumnListServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.columnList = response.data.Data;
          this.getColumnListServiceState.success = true;
        }
      })
      .catch((err: string) => {
        this.getColumnListServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getColumnListServiceState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allOrderTableView(): IOrderTableList[] {
    if (this.orderList?.Trays && this.orderList.Trays?.length > 0) {
      return this.orderList.Trays.map((tray) => {
        const processDueDateMap = Object.fromEntries(
          tray.ActiveTrayProcessDue.map((process) => {
            const key = process.ProcessId;
            let value: string;
            if (process.IsComplete) {
              value = 'done';
            } else if (process.DueDate !== '0001-01-01') {
              value = process.DisplayDueDate;
            } else {
              value = 'N/A';
            }
            return [key, value];
          })
        );

        return {
          TrayId: tray.TrayId,
          Id: tray.Id,
          EndUserName: tray.EndUserName,
          EndUserOrderId: tray.EndUserOrderId,
          OrderId: tray.OrderId,
          Kishu: tray.Kishu,
          ProductId: tray.ProductId,
          Katashiki1: tray.Katashiki1,
          MaterialKatashiki: tray.MaterialKatashiki,
          Katashiki2: tray.Katashiki2,
          MaterialProductId: tray.MaterialProductId,
          Quantity: tray.Quantity,
          SubContractor: tray.SubContractor,
          ProcessName: tray.ProcessName,
          ProductComment1: tray.ProductComment1 || ' ',
          ProductComment2: tray.ProductComment2 || ' ',
          ProductComment3: tray.ProductComment3 || ' ',
          ProductComment4: tray.ProductComment4 || ' ',
          ProductComment5: tray.ProductComment5 || ' ',
          ProductComment6: tray.ProductComment6 || ' ',
          NextProcess: tray.NextProcess,
          NextProcessName: tray.NextProcess?.ProcessName ?? '',
          IsPermission: tray.NextProcess?.IsPermission ?? false,
          CustomerDueDate: fixTimezoneOffsetYYYYMMDD(tray.CustomerDueDate),
          PreviousProcess: tray.PreviousProcess || '',
          isSelected: tray.isSelected,
          ActiveTrayProcessDue: [],
          ...processDueDateMap,
        };
      });
    }
  }

  get allProcessHeaders(): ProcessList[] {
    const processHeaders: ProcessList[] = [];
    if (this.orderList?.ProcessList?.length > 0) {
      this.orderList.ProcessList.map((process) => {
        processHeaders.push({
          Type: process.Type,
          Id: process.Id,
          Hide: process.Hide,
          ProcessName: process.ProcessName,
          ShortName: process.ShortName,
        });
      });
    }
    return processHeaders;
  }

  get allColumnOptions(): IOption[] {
    const options: IOption[] = [
      {
        id: -1,
        value: 'please_select',
      },
    ];

    return options;
  }

  get LEDFormInitialValue(): any {
    return {
      ColorCode: '-1',
      Second: '10s',
    };
  }

  get TimerOptions(): IOption[] {
    return timerOptions;
  }

  get allHKRDashboardData() {
    let data = [];

    if (
      this.hkrDashboardDataList.HKRTableData &&
      this.hkrDashboardDataList.HKRTableData.length > 0
    ) {
      data = this.hkrDashboardDataList.HKRTableData.map((item, index) => {
        let processData = {};
        item.HKRProcessList.forEach((process) => {
          processData[process.ProcessShortName] = process.count;
        });

        return {
          Id: index + 1,
          Kishu: item.Kishu,
          ProcessDueDate: fixTimezoneOffsetYYYYMMDD(item.ProcessDueDate),
          Code: item.Code,
          Remaining: item.Remaining,
          ...processData,
        };
      });
    }

    return data;
  }

  get allProcesses() {
    if (
      this.hkrDashboardDataList.HKRTableData &&
      this.hkrDashboardDataList.HKRTableData.length > 0
    ) {
      return this.hkrDashboardDataList.HKRTableData[0].HKRProcessList.map(
        (process) => ({
          label: process.ProcessShortName,
          value: process.ProcessShortName,
        })
      );
    }
    return [];
  }

  get columnListDetails() {
    const list: IMultiSelectOption[] = [];
    if (this.columnList && this.columnList.length > 0) {
      this.columnList
        .filter((col) => col !== 'Planed Due Date') // Hide the Planed Due Date Column
        .map((column) =>
          list.push({
            // label: column.toLowerCase().replace(/\s+/g, '_'),
            // label: column.replace(/\s+/g, '_'),
            label: column,
            // value: column.replace(/ /g, ''),
            value: column,
          })
        );
    }
    return list;
  }

  get visibleHKRColumnsList() {
    if (
      this.hkrDashboardDataList.SavedColumn &&
      this.hkrDashboardDataList.SavedColumn.length > 0
    ) {
      return this.hkrDashboardDataList.SavedColumn.split(',').map(
        (process) => ({
          label: process,
          value: process,
        })
      );
    }
    return [];
  }

  get visibleOrderTableColumnsList() {
    if (this.orderList.SavedColumn && this.orderList.SavedColumn.length > 0) {
      return this.orderList.SavedColumn.split(',').map((opt) => ({
        label: opt,
        value: opt,
      }));
    }
    return [];
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.orderTableViewList = GetAllOrderTableViewState;
  };

  resetGetOrderTableListServiceState = () => {
    this.getOrderTableListServiceState = { ...this.initialStateValue };
  };

  resetLedBlinkState = () => {
    this.ledBlinkState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.orderTableViewList = GetAllOrderTableViewState;
    this.getAllOrderTableViewState = { ...this.initialStateValue };
    this.getOrderTableListServiceState = { ...this.initialStateValue };
    this.orderList = GetAllOrderTableState;
    this.ledBlinkState = { ...this.initialStateValue };
    this.getHKRDashboardDataServiceState = { ...this.initialStateValue };
    this.hkrDashboardDataList = GetAllHKRDashboardDataState;
    this.getColumnListServiceState = { ...this.initialStateValue };
    this.columnList = undefined;
  };

  //#endregion
}

export default new DashboardStore();
